import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import API from "../../api/client";
import {
  LayoutDashboard, ListTodo, CalendarDays, Bell, Users, Settings, FolderOpen, LogOut, ChevronLeft, ChevronRight, Archive
} from "lucide-react";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/tasks", icon: ListTodo, label: "Tasks" },
  { to: "/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
  { to: "/groups", icon: FolderOpen, label: "Groups" },
  { to: "/archive", icon: Archive, label: "Archive" },
];

const adminItems = [
  { to: "/users", icon: Users, label: "Users" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [company, setCompany] = useState({ name: "", logo_path: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    API.get("/settings/company").then(r => setCompany(r.data)).catch(() => {});
  }, [location.pathname]);

  // Poll unread notification count
  useEffect(() => {
    const fetchCount = () => API.get("/notifications/count").then(r => setUnreadCount(r.data.count)).catch(() => {});
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    if (company.logo_path) {
      API.get("/settings/company/logo-file", { responseType: "blob" })
        .then(r => setLogoUrl(URL.createObjectURL(r.data)))
        .catch(() => setLogoUrl(""));
    } else {
      setLogoUrl("");
    }
  }, [company.logo_path]);

  const linkClass = (isActive) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
      isActive
        ? "bg-[#2B2D31] text-white font-medium"
        : "text-[#8A8F98] hover:bg-[#1A1B20] hover:text-white"
    }`;

  return (
    <div
      data-testid="sidebar"
      className={`flex flex-col h-screen bg-[#111216] border-r border-[#27282D] transition-all duration-200 ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Logo / Company - white background, full width */}
      <div className="flex items-center justify-center bg-white border-b border-[#27282D] p-2" style={{ minHeight: collapsed ? "48px" : "56px" }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className={`object-contain ${collapsed ? "max-h-8 max-w-[48px]" : "max-h-10 w-full"}`}
            data-testid="company-logo-img"
          />
        ) : company.name ? (
          <span className={`font-bold font-['Manrope'] text-[#111216] truncate ${collapsed ? "text-xs" : "text-sm"}`} data-testid="company-name">
            {collapsed ? company.name[0].toUpperCase() : company.name}
          </span>
        ) : (
          <span className={`font-bold font-['Manrope'] text-[#111216] ${collapsed ? "text-xs" : "text-sm"}`}>
            {collapsed ? "T" : "Task Manager"}
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {!collapsed && (
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">
            Workspace
          </div>
        )}
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`sidebar-${item.label.toLowerCase()}-link`}
            className={({ isActive }) => linkClass(isActive)}
          >
            <div className="relative flex-shrink-0">
              <item.icon className="w-4 h-4" />
              {item.label === "Notifications" && unreadCount > 0 && (
                <span
                  data-testid="sidebar-notification-badge"
                  className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-bold text-white bg-[#E24A4A] rounded-full"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
            {!collapsed && (
              <span className="flex items-center gap-2">
                {item.label}
                {item.label === "Notifications" && unreadCount > 0 && (
                  <span
                    data-testid="sidebar-notification-count"
                    className="h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] font-bold text-white bg-[#E24A4A] rounded-full"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </span>
            )}
          </NavLink>
        ))}

        {user?.role === "admin" && (
          <>
            {!collapsed && (
              <div className="px-3 mt-6 mb-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">
                Admin
              </div>
            )}
            {adminItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`sidebar-${item.label.toLowerCase()}-link`}
                className={({ isActive }) => linkClass(isActive)}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Bottom */}
      <div className="border-t border-[#27282D] p-2 space-y-1">
        <button
          onClick={onToggle}
          data-testid="sidebar-toggle"
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[#8A8F98] hover:bg-[#1A1B20] hover:text-white w-full transition-colors"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          {!collapsed && <span>Collapse</span>}
        </button>
        <button
          onClick={logout}
          data-testid="sidebar-logout-btn"
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[#8A8F98] hover:bg-[#1A1B20] hover:text-red-400 w-full transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}
